import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  promoteWorkspace,
  getDeliverable,
  logWorkspaceEvent,
} from "@/lib/api";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Encapsulates the Promote-to-Client workflow:
 *   - kick off generation via POST /api/workspaces/:id/promote
 *   - poll the briefing deliverable until ready/failed
 *   - own the share-email copy + mailto helpers
 *
 * The caller (WorkspaceDetail.jsx) supplies `workspaceId` + a `refresh`
 * function so we can re-fetch the parent data once the briefing lands.
 */
export function useWorkspacePromote({ workspaceId, refresh, refreshEvents }) {
  const [showPromote, setShowPromote] = useState(false);
  const [audienceNote, setAudienceNote] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [result, setResult] = useState(null);
  const [emailCopied, setEmailCopied] = useState(false);

  const open = useCallback(() => setShowPromote(true), []);

  const close = useCallback(() => {
    setShowPromote(false);
    setResult(null);
    setAudienceNote("");
    setEmailCopied(false);
  }, []);

  const promote = useCallback(async () => {
    setPromoting(true);
    try {
      const res = await promoteWorkspace(workspaceId, audienceNote);
      setResult({ ...res, status: "generating" });
      const start = Date.now();
      while (true) {
        if (Date.now() - start > POLL_TIMEOUT_MS) break;
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const d = await getDeliverable(res.deliverable_id);
        if (d.status === "ready" || d.status === "failed") {
          setResult((prev) => ({ ...prev, status: d.status, error: d.error }));
          break;
        }
      }
      await refresh();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Promote failed"
      );
    } finally {
      setPromoting(false);
    }
  }, [workspaceId, audienceNote, refresh]);

  const copyEmailBody = useCallback(() => {
    if (!result) return;
    const fullUrl = `${window.location.origin}${result.share_url_path}`;
    const body = result.share_email_body.replace(result.share_url_path, fullUrl);
    const text = `Subject: ${result.share_email_subject}\n\n${body}`;
    navigator.clipboard.writeText(text).catch(() => {});
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2500);
    logWorkspaceEvent(workspaceId, "email_copied", {
      subject: result.share_email_subject,
    }).then(() => refreshEvents());
  }, [result, workspaceId, refreshEvents]);

  const openInEmailClient = useCallback(() => {
    if (!result) return;
    const fullUrl = `${window.location.origin}${result.share_url_path}`;
    const body = result.share_email_body.replace(result.share_url_path, fullUrl);
    const mailto =
      `mailto:?subject=${encodeURIComponent(result.share_email_subject)}` +
      `&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    logWorkspaceEvent(workspaceId, "email_opened_in_client", {
      subject: result.share_email_subject,
    }).then(() => refreshEvents());
  }, [result, workspaceId, refreshEvents]);

  return {
    showPromote,
    open,
    close,
    audienceNote,
    setAudienceNote,
    promoting,
    result,
    emailCopied,
    promote,
    copyEmailBody,
    openInEmailClient,
  };
}
